import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  BillingHistoryInvoice,
  BillingHistoryLineItem,
  BillingHistoryPayment,
  BillingOwnerType,
} from './billingHistory';

export const CUSTOMER_BILLING_PAYMENT_SELECT = [
  'id',
  'payment_type',
  'status',
  'total_sar',
  'created_at',
].join(',');

// This is the complete browser-visible invoice contract for normal customers.
// JSON paths project only the immutable business identity fields used by the UI;
// raw invoice/ERP metadata and internal accounting identities never leave PostgREST.
export const CUSTOMER_BILLING_INVOICE_SELECT = [
  'id',
  'invoice_number',
  'payment_transaction_id',
  'owner_type',
  'amount_sar',
  'vat_amount_sar',
  'vat_rate_percent',
  'total_sar',
  'status',
  'issued_at',
  'paid_at',
  'created_at',
  'billing_buyer_type:confirmed_billing_snapshot_json->>buyer_type',
  'billing_legal_customer_name:confirmed_billing_snapshot_json->>legal_customer_name',
  'billing_vat_number:confirmed_billing_snapshot_json->>vat_number',
  'billing_additional_id_scheme:confirmed_billing_snapshot_json->>additional_id_scheme',
  'billing_additional_id_value:confirmed_billing_snapshot_json->>additional_id_value',
  'billing_commercial_registration_number:confirmed_billing_snapshot_json->>commercial_registration_number',
  'invoice_lines(line_number,description,quantity,unit_price_sar,total_sar)',
  'erp_invoice_map(external_invoice_number)',
  'invoice_official_artifact_current(artifact_type)',
  'invoice_official_email_deliveries(delivery_state)',
].join(',');

export interface CustomerBillingInvoiceRow extends Omit<BillingHistoryInvoice, 'line_items' | 'billing_identity' | 'official_artifacts'> {
  billing_buyer_type?: string | null;
  billing_legal_customer_name?: string | null;
  billing_vat_number?: string | null;
  billing_additional_id_scheme?: string | null;
  billing_additional_id_value?: string | null;
  billing_commercial_registration_number?: string | null;
  invoice_lines?: BillingHistoryLineItem[] | null;
  invoice_official_artifact_current?: Array<{ artifact_type?: string | null }> | null;
  invoice_official_email_deliveries?: Array<{ delivery_state?: string | null }> | null;
}

export interface CustomerBillingHistoryResult {
  payments: BillingHistoryPayment[];
  invoices: BillingHistoryInvoice[];
}

export interface CustomerBillingHistoryRequest {
  ownerType: BillingOwnerType;
  ownerId: string;
  limit?: number;
}

export function mapCustomerBillingInvoice(row: CustomerBillingInvoiceRow): BillingHistoryInvoice {
  const lineItems = [...(row.invoice_lines ?? [])]
    .sort((a, b) => Number(a.line_number) - Number(b.line_number))
    .map((line) => ({
      line_number: Number(line.line_number),
      description: String(line.description),
      quantity: Number(line.quantity),
      unit_price_sar: Number(line.unit_price_sar),
      total_sar: Number(line.total_sar),
    }));

  return {
    id: row.id,
    invoice_number: row.invoice_number,
    payment_transaction_id: row.payment_transaction_id,
    owner_type: row.owner_type,
    amount_sar: row.amount_sar,
    vat_amount_sar: row.vat_amount_sar,
    vat_rate_percent: row.vat_rate_percent,
    total_sar: row.total_sar,
    status: row.status,
    issued_at: row.issued_at,
    paid_at: row.paid_at,
    created_at: row.created_at,
    line_items: lineItems,
    billing_identity: {
      buyer_type: row.billing_buyer_type ?? null,
      legal_customer_name: row.billing_legal_customer_name ?? null,
      vat_number: row.billing_vat_number ?? null,
      additional_id_scheme: row.billing_additional_id_scheme ?? null,
      additional_id_value: row.billing_additional_id_value ?? null,
      commercial_registration_number: row.billing_commercial_registration_number ?? null,
    },
    erp_invoice_map: (row.erp_invoice_map ?? []).map((reference) => ({
      external_invoice_number: reference.external_invoice_number ?? null,
    })),
    official_artifacts: (row.invoice_official_artifact_current ?? [])
      .filter((artifact) => typeof artifact.artifact_type === 'string')
      .map((artifact) => ({ artifact_type: artifact.artifact_type! })),
    official_email_deliveries: (row.invoice_official_email_deliveries ?? [])
      .filter((delivery) => typeof delivery.delivery_state === 'string')
      .map((delivery) => ({ delivery_state: delivery.delivery_state! })),
  };
}

export async function fetchCustomerBillingHistory(
  client: SupabaseClient,
  request: CustomerBillingHistoryRequest,
): Promise<CustomerBillingHistoryResult> {
  let paymentQuery = client
    .from('payment_transactions')
    .select(CUSTOMER_BILLING_PAYMENT_SELECT)
    .eq('owner_type', request.ownerType)
    .eq('owner_id', request.ownerId)
    .neq('status', 'pending')
    .order('created_at', { ascending: false })
    .order('id', { ascending: false });

  if (request.limit != null) paymentQuery = paymentQuery.limit(request.limit);

  const { data: paymentRows, error: paymentError } = await paymentQuery;
  if (paymentError) throw paymentError;

  const payments = (paymentRows ?? []) as unknown as BillingHistoryPayment[];
  const paymentIds = payments.map((payment) => payment.id);
  if (paymentIds.length === 0) return { payments, invoices: [] };

  const { data: invoiceRows, error: invoiceError } = await client
    .from('invoices')
    .select(CUSTOMER_BILLING_INVOICE_SELECT)
    .eq('owner_type', request.ownerType)
    .eq('owner_id', request.ownerId)
    .in('payment_transaction_id', paymentIds)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false });

  if (invoiceError) throw invoiceError;
  return {
    payments,
    invoices: ((invoiceRows ?? []) as unknown as CustomerBillingInvoiceRow[]).map(mapCustomerBillingInvoice),
  };
}
